import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { ArrowUpLeft, Gift } from 'lucide-react';
import type { LinkItem, LinkTag } from '@/data/links';
import { Iris } from './Iris';
import { emitZap, rectCenter, restingCenter } from './zapEvents';
import './redesign.css';

const TAG_LABEL: Partial<Record<LinkTag, string>> = {
  free: 'חינם', freemium: 'חינם בסיסי', deal: 'דיל שותפים', new: 'חדש', popular: 'פופולרי',
  recommended: 'מומלץ', beta: 'בטא', premium: 'פרימיום', 'open-source': 'קוד פתוח',
  israeli: 'ישראלי', 'news-channel': 'ערוץ חדשות', 'quiet-news': 'שקט'
};

export const PREVIEW_VARIANTS = ['זכוכית', 'עינית', 'הולוגרמה', 'טרמינל', 'בלי מסגרת', 'חפיסה'] as const;

const IDLE_COLOR = '#8FE3F0';
const HINT = 'העבירו את הסמן על כרטיס — והוא ייפתח כאן.';

const domainOf = (u: string) => {
  try { return new URL(u).hostname.replace(/^www\./, ''); } catch { return ''; }
};

interface LinkPreviewProps {
  link: LinkItem | null;
  /** The hovered card; the zap flies from its icon into the preview's focal point. */
  originEl: HTMLElement | null;
  sectionTitle: string;
  /** Shown while nothing is hovered, rotating through the community links. */
  featured: LinkItem | null;
  featuredTitle: string;
  featuredIndex: number;
  featuredCount: number;
  variant: number;
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onInteractionChange?: (paused: boolean) => void;
  onLinkClick?: (id: string) => void;
}

interface VariantProps {
  link: LinkItem | null;
  color: string;
  sectionTitle: string;
  targetRef: RefObject<HTMLDivElement>;
}

const glass = (color: string, active: boolean): CSSProperties => ({
  background: `linear-gradient(150deg, ${color}${active ? '1a' : '0a'} 0%, rgba(8,12,30,${active ? 0.3 : 0.16}) 60%)`,
  border: `1px solid ${active ? `${color}50` : 'rgba(214,226,255,0.11)'}`,
  backdropFilter: 'blur(1.5px)',
  WebkitBackdropFilter: 'blur(1.5px)',
  textShadow: '0 1px 10px rgba(6,11,30,.9)'
});

const OpenButton = ({ link, color, children = 'פתיחה', className = '' }: { link: LinkItem; color: string; children?: ReactNode; className?: string }) => (
  <a
    href={link.url}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={`פתיחת ${link.title} בחלון חדש`}
    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-[13.5px] text-white transition-[transform,box-shadow] duration-200 hover:-translate-y-px ${className}`}
    style={{ background: `linear-gradient(135deg, ${color}cc, ${color}80)`, boxShadow: `0 8px 26px -10px ${color}` }}
  >
    {children}
    <ArrowUpLeft className="w-4 h-4" strokeWidth={2} aria-hidden="true" />
  </a>
);

const Meta = ({ link, color, center = false }: { link: LinkItem; color: string; center?: boolean }) => {
  const tag = link.tag ? TAG_LABEL[link.tag] : undefined;
  return (
    <>
      <div className={`flex flex-wrap items-center gap-2 mt-4 ${center ? 'justify-center' : ''}`}>
        {tag && <span className="text-[10.5px] tracking-wider px-2 py-0.5 rounded-full border" style={{ color, borderColor: `${color}60` }}>{tag}</span>}
        <span dir="ltr" className="rd-mono text-[11.5px] text-[#8FA0C8]">{domainOf(link.url)}</span>
      </div>
      {link.affiliateBenefit && (
        <div className={`mt-3 text-[13px] flex items-center gap-1.5 ${center ? 'justify-center' : ''}`} style={{ color }}>
          <Gift className="w-3.5 h-3.5" aria-hidden="true" />
          {link.affiliateBenefit}
        </div>
      )}
    </>
  );
};

const IconTile = ({ link, color, size = 56 }: { link: LinkItem; color: string; size?: number }) => {
  const Icon = link.icon;
  return (
    <span className="rd-pico rounded-2xl grid place-items-center" style={{ width: size, height: size, background: `${color}26` }}>
      <Icon style={{ color, width: size / 2, height: size / 2 }} strokeWidth={1.6} />
    </span>
  );
};

/* 1 — glass, the iris reveals the icon */
const GlassVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => (
  <div className="relative rounded-2xl overflow-hidden transition-[background,border-color] duration-500" style={glass(color, !!link)}>
    <div className="absolute top-3 bottom-3 right-0 w-[2px] rounded-full" style={{ background: link ? color : `${color}40`, boxShadow: link ? `0 0 10px ${color}70` : 'none' }} />
    <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}80, transparent)` }} />
    <div className="relative p-7 min-h-[380px] flex flex-col items-center text-center">
      <div ref={targetRef}>
        <Iris size={132} cycle={link?.id ?? 'idle'} openTo={link ? 1 : 0.42} duration={link ? 1.1 : 2.4}>
          {link ? <IconTile link={link} color={color} /> : <span className="w-3 h-3 rounded-full bg-[#8FE3F0] shadow-[0_0_16px_rgba(143,227,240,.9)]" />}
        </Iris>
      </div>
      {!link ? (
        <div className="mt-6">
          <div className="text-[20px] font-semibold text-white/90">{sectionTitle}</div>
          <p className="mt-3 text-sm text-white/55 leading-relaxed max-w-[28ch]">{HINT}</p>
        </div>
      ) : (
        <div key={link.id} className="rd-pv-card mt-6 w-full">
          <div className="text-[21px] font-bold leading-tight" style={{ color }}>{link.title}</div>
          {link.subtitle && <div className="text-sm text-white/75 mt-1.5">{link.subtitle}</div>}
          <div className="h-px w-12 mx-auto my-4" style={{ background: `${color}45` }} />
          {link.description && <p className="text-[13.5px] text-white/60 leading-[1.75]">{link.description}</p>}
          <Meta link={link} color={color} center />
          <OpenButton link={link} color={color} className="mt-6" />
        </div>
      )}
    </div>
  </div>
);

/* 2 — viewfinder: corner brackets, crosshair, HUD labels */
const ViewfinderVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => {
  const corner = 'absolute w-5 h-5 border-[#EDEEF2]/70';
  return (
    <div className="relative min-h-[400px] p-8 flex flex-col items-center text-center" style={{ textShadow: '0 1px 10px rgba(6,11,30,.9)' }}>
      <span className={`${corner} top-0 right-0 border-t border-r`} />
      <span className={`${corner} top-0 left-0 border-t border-l`} />
      <span className={`${corner} bottom-0 right-0 border-b border-r`} />
      <span className={`${corner} bottom-0 left-0 border-b border-l`} />
      <div dir="ltr" className="rd-mono absolute top-3 inset-x-8 flex justify-between text-[10px] tracking-widest text-[#8FA0C8]">
        <span className="flex items-center gap-1.5"><i className="w-1.5 h-1.5 rounded-full bg-[#E4705E] animate-pulse" />REC</span>
        <span>{link ? domainOf(link.url) : 'STANDBY'}</span>
      </div>
      <div className="relative mt-6" ref={targetRef}>
        <span className="absolute left-1/2 -top-4 -bottom-4 w-px bg-white/15" />
        <span className="absolute top-1/2 -left-6 -right-6 h-px bg-white/15" />
        <div className="relative w-[120px] h-[120px] rounded-full border grid place-items-center transition-colors duration-500" style={{ borderColor: `${color}70`, boxShadow: `0 0 30px ${color}25 inset` }}>
          {link ? <IconTile link={link} color={color} size={60} /> : <span className="w-2.5 h-2.5 rounded-full" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />}
        </div>
      </div>
      {!link ? (
        <div className="mt-8">
          <div className="text-[20px] font-semibold text-white/90">{sectionTitle}</div>
          <p className="mt-3 text-sm text-white/55 max-w-[28ch]">{HINT}</p>
        </div>
      ) : (
        <div key={link.id} className="rd-pv-card mt-7 w-full">
          <div className="text-[21px] font-bold leading-tight text-white">{link.title}</div>
          {link.subtitle && <div className="text-sm mt-1.5" style={{ color }}>{link.subtitle}</div>}
          {link.description && <p className="mt-4 text-[13.5px] text-white/60 leading-[1.75]">{link.description}</p>}
          <Meta link={link} color={color} center />
          <OpenButton link={link} color={color} className="mt-6" />
        </div>
      )}
      <div dir="ltr" className="rd-mono absolute bottom-3 inset-x-8 flex justify-between text-[10px] tracking-widest text-[#8FA0C8]/70">
        <span>ISO 400</span><span>ƒ/1.4</span><span>1/250</span>
      </div>
    </div>
  );
};

/* 3 — holographic ticket: tilts toward the pointer, sheen follows it */
const TicketVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => {
  const [tilt, setTilt] = useState({ rx: 0, ry: 0, mx: 50, my: 50 });
  return (
    <div
      className="[perspective:900px]"
      onPointerMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
        setTilt({ rx: (0.5 - py) * 10, ry: (px - 0.5) * 12, mx: px * 100, my: py * 100 });
      }}
      onPointerLeave={() => setTilt({ rx: 0, ry: 0, mx: 50, my: 50 })}
    >
      <div
        className="relative rounded-[20px] overflow-hidden transition-transform duration-200 ease-out"
        style={{ ...glass(color, !!link), transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)` }}
      >
        <div
          className="absolute inset-0 pointer-events-none mix-blend-screen opacity-60"
          style={{ background: `radial-gradient(circle at ${tilt.mx}% ${tilt.my}%, rgba(255,255,255,.18), transparent 40%), linear-gradient(${115 + tilt.ry * 4}deg, transparent 20%, #8FE3F022 35%, #E4705E1f 50%, #7C9CFF22 65%, transparent 80%)` }}
        />
        <div className="relative p-7 flex items-center gap-4" ref={targetRef}>
          {link ? <IconTile link={link} color={color} size={52} /> : <span className="w-[52px] h-[52px] rounded-2xl border border-white/15" />}
          <div className="min-w-0 text-right">
            <div className="rd-mono text-[10px] tracking-[.25em] text-[#8FA0C8]">ADMIT ONE · NVISION</div>
            <div className="text-[19px] font-bold leading-tight mt-1" style={{ color: link ? color : '#EDEEF2' }}>{link ? link.title : sectionTitle}</div>
          </div>
        </div>
        <div className="relative h-0 border-t border-dashed border-white/20 mx-5">
          <span className="absolute -right-8 -top-3 w-6 h-6 rounded-full bg-[#060B1E]" />
          <span className="absolute -left-8 -top-3 w-6 h-6 rounded-full bg-[#060B1E]" />
        </div>
        <div className="relative p-7 min-h-[200px]">
          {!link ? (
            <p className="text-sm text-white/55">{HINT}</p>
          ) : (
            <div key={link.id} className="rd-pv-card">
              {link.subtitle && <div className="text-sm text-white/80">{link.subtitle}</div>}
              {link.description && <p className="mt-3 text-[13.5px] text-white/60 leading-[1.75]">{link.description}</p>}
              <Meta link={link} color={color} />
              <OpenButton link={link} color={color} className="mt-6" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* 4 — terminal: the description types itself out */
const TerminalVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => {
  const [typed, setTyped] = useState(0);
  const text = link?.description ?? '';
  useEffect(() => {
    setTyped(0);
    if (!text) return;
    const id = setInterval(() => setTyped((n) => (n >= text.length ? n : n + 2)), 14);
    return () => clearInterval(id);
  }, [text]);
  return (
    <div className="rounded-xl overflow-hidden border border-white/10" style={{ background: 'rgba(6,10,24,.55)', backdropFilter: 'blur(1.5px)' }}>
      <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10" dir="ltr">
        <i className="w-2.5 h-2.5 rounded-full bg-[#E4705E]/80" /><i className="w-2.5 h-2.5 rounded-full bg-[#D9A441]/80" /><i className="w-2.5 h-2.5 rounded-full bg-[#3FBFA0]/80" />
        <span className="rd-mono text-[11px] text-white/40 ml-3">nvision — zsh</span>
      </div>
      <div className="rd-mono p-5 min-h-[340px] text-[13px] leading-[1.8]">
        <div dir="ltr" className="text-white/45" ref={targetRef}>
          <span style={{ color }}>➜</span> ~ nvision open {link ? domainOf(link.url) : `--section "${sectionTitle}"`}
        </div>
        {!link ? (
          <div className="mt-3 text-white/55">{HINT}<span className="inline-block w-2 h-4 align-middle bg-white/60 animate-pulse mr-1" /></div>
        ) : (
          <div key={link.id} className="mt-3">
            <div className="text-[15px] font-medium" style={{ color }}># {link.title}</div>
            {link.subtitle && <div className="text-white/75">{link.subtitle}</div>}
            <div className="mt-3 text-white/60">
              {text.slice(0, typed)}
              <span className="inline-block w-2 h-4 align-middle animate-pulse mr-1" style={{ background: color }} />
            </div>
            <Meta link={link} color={color} />
            <a href={link.url} target="_blank" rel="noopener noreferrer" className="inline-block mt-6 px-3 py-1.5 rounded-md border text-[12.5px] hover:bg-white/5" style={{ color, borderColor: `${color}70` }}>
              [ENTER] פתיחה ↗
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

/* 5 — frameless: floats on the sky, only the button has a surface */
const FramelessVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => (
  <div className="relative pr-6 min-h-[380px] pt-2" style={{ textShadow: '0 1px 14px rgba(6,11,30,1)' }}>
    <span className="absolute right-0 top-0 bottom-0 w-px" style={{ background: `linear-gradient(180deg, transparent, ${color}, transparent)`, boxShadow: `0 0 12px ${color}` }} />
    <div ref={targetRef} className="w-fit">
      {link ? (
        <div className="relative">
          <span className="absolute -inset-6 rounded-full blur-2xl" style={{ background: `${color}30` }} />
          <IconTile link={link} color={color} size={72} />
        </div>
      ) : <span className="block w-3 h-3 rounded-full" style={{ background: color, boxShadow: `0 0 16px ${color}` }} />}
    </div>
    {!link ? (
      <div className="mt-8">
        <div className="text-[26px] font-semibold text-white/90">{sectionTitle}</div>
        <p className="mt-3 text-white/55 max-w-[30ch]">{HINT}</p>
      </div>
    ) : (
      <div key={link.id} className="rd-pv-card mt-8">
        <div className="text-[30px] font-bold leading-[1.15] text-white">{link.title}</div>
        {link.subtitle && <div className="text-[15px] mt-2" style={{ color }}>{link.subtitle}</div>}
        {link.description && <p className="mt-5 text-[15px] text-white/65 leading-[1.8] max-w-[40ch]">{link.description}</p>}
        <Meta link={link} color={color} />
        <OpenButton link={link} color={color} className="mt-7" />
      </div>
    )}
  </div>
);

/* 6 — deck: the glass card with two cards peeking behind; flips on change */
const DeckVariant = ({ link, color, sectionTitle, targetRef }: VariantProps) => {
  const Pip = link?.icon;
  return (
  <div className="relative [perspective:1000px] pt-3">
    <div className="absolute inset-x-4 top-0 bottom-6 rounded-2xl border border-white/10 bg-white/[0.02]" style={{ transform: 'rotate(-3.5deg)' }} />
    <div className="absolute inset-x-2 top-1.5 bottom-3 rounded-2xl border border-white/10 bg-white/[0.03]" style={{ transform: 'rotate(2.5deg)' }} />
    <div key={link?.id ?? 'idle'} className="relative rounded-2xl overflow-hidden" style={{ ...glass(color, !!link), animation: 'rd-flip .55s cubic-bezier(.2,.8,.3,1)' }}>
      <div className="absolute top-3 bottom-3 right-0 w-[2px] rounded-full" style={{ background: color, boxShadow: `0 0 10px ${color}70` }} />
      <div className="relative p-7 min-h-[360px]">
        <div ref={targetRef} data-rd-zap className="w-fit">
          {link ? <IconTile link={link} color={color} size={56} /> : <span className="block w-14 h-14 rounded-2xl border border-white/15" />}
        </div>
        {Pip && (
          <span aria-hidden="true" className="absolute bottom-4 left-4 opacity-55" style={{ color }}>
            <Pip className="w-4 h-4" strokeWidth={1.6} />
          </span>
        )}
        {!link ? (
          <div className="mt-6">
            <div className="text-[20px] font-semibold text-white/90">{sectionTitle}</div>
            <p className="mt-3 text-sm text-white/55 max-w-[28ch]">{HINT}</p>
          </div>
        ) : (
          <div className="mt-6">
            <div className="text-[21px] font-bold leading-tight" style={{ color }}>{link.title}</div>
            {link.subtitle && <div className="text-sm text-white/75 mt-1.5">{link.subtitle}</div>}
            {link.description && <p className="mt-4 text-[13.5px] text-white/60 leading-[1.75]">{link.description}</p>}
            <Meta link={link} color={color} />
            <OpenButton link={link} color={color} className="mt-6" />
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

const VARIANTS = [GlassVariant, ViewfinderVariant, TicketVariant, TerminalVariant, FramelessVariant, DeckVariant];

/** Sticky desktop side panel; `variant` picks one of the candidate designs. */
export const LinkPreview = ({
  link, originEl, sectionTitle, featured, featuredTitle, featuredIndex, featuredCount, variant, onPointerEnter, onPointerLeave, onInteractionChange, onLinkClick
}: LinkPreviewProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  useEffect(() => { onInteractionChange?.(hovered || focused); }, [hovered, focused, onInteractionChange]);

  useEffect(() => {
    if (!link || !originEl || !targetRef.current) return;
    const from = originEl.querySelector('[data-rd-icon]') ?? originEl;
    const target = targetRef.current;
    const icon = (target.querySelector('[data-rd-zap]') ?? target) as HTMLElement;
    emitZap(rectCenter(from), restingCenter(icon));
  }, [link, originEl]);

  const shown = link ?? featured;
  const color = shown?.color ?? IDLE_COLOR;
  const View = VARIANTS[variant % VARIANTS.length];
  const isFeatured = !link && !!featured;

  return (
    <aside aria-label="תצוגה מקדימה של קישור" onPointerEnter={() => { setHovered(true); onPointerEnter(); }} onPointerLeave={() => { setHovered(false); onPointerLeave(); }}
      onFocus={() => setFocused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setFocused(false); }}
      onClickCapture={(event) => { if (shown && (event.target as HTMLElement).closest('a[href]')) onLinkClick?.(shown.id); }}
    >
      <div className={`flex items-center gap-2 mb-3 text-xs transition-opacity duration-300 ${isFeatured ? 'opacity-100' : 'opacity-0'}`}>
        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: color, boxShadow: `0 0 8px ${color}` }} />
        <span className="text-white/70">מומלץ מ{featuredTitle}</span>
        <span className="flex gap-1 mr-auto" aria-hidden="true">
          {Array.from({ length: featuredCount }, (_, i) => (
            <i key={i} className="w-1 h-1 rounded-full transition-colors" style={{ background: i === featuredIndex ? color : 'rgba(255,255,255,.2)' }} />
          ))}
        </span>
      </div>
      <View link={shown} color={color} sectionTitle={sectionTitle} targetRef={targetRef} />
    </aside>
  );
};
